import { benchmark } from 'shared/containers/App/saga';
import { call, CallEffect, ForkEffect, put, PutEffect, select, SelectEffect, takeLatest } from 'redux-saga/effects';
import { GraphQLError } from 'graphql';
import {
  CartItems,
  CartProduct,
  GetAddOnsPayload,
  ShoppingCart,
  ShoppingCartAddonsInput,
} from '@buy-viasat/types/build/bv';
import { selectServiceAddressValues } from '@buy-viasat/redux/src/serviceability';
import { cartActions, selectAddonToBeRemoved, selectCartId, selectCartItems } from '@buy-viasat/redux/src/cart';
import removeCartItemsAsync, { RemoveCartItemsResponse } from '../../providers/requests/cart/removeCartItems';
import clientLogger from '../../utils/clientLogger';
import upsertCartAddonsAsync, { UpsertCartAddonsResponse } from '../../providers/requests/cart/upsertCartAddons';
import { upsertCartAddonsRest } from '../../providers/requests/cart/upsertCartAddonsRest';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import { Routes, RouteStates } from '@buy-viasat/redux/src/navigator';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  addonActions,
  DataLayerEventType,
  selectAddonsToUpsert,
  selectAvailableAddons,
  selectGroupedAddonIds,
} from '@buy-viasat/redux/src/addons';
import { adobeTargetDataLayer } from 'shared/containers/Analytics';
import { DataLayerEvents } from 'shared/containers/Analytics/types';
import {
  appActions,
  Features,
  Modals,
  selectCountry,
  selectCustomerType,
  selectFeatureFlags,
} from '@buy-viasat/redux/src/app';
import { AddressType } from '@buy-viasat/redux/src/address';
import { consentActions } from 'shared/containers/disclosures/redux/slice';

export function* generateCart(data: ShoppingCart): Generator<PutEffect | SelectEffect, void, Features> {
  yield put(cartActions.setCartProperties(data));
}

export function* generateError(
  error: readonly GraphQLError[],
  apiCall: string,
): Generator<CallEffect | PutEffect, void, void> {
  yield call(clientLogger, `${apiCall}: `, error);
  yield put(cartActions.setIsCartError(true));
}

export function* upsertAddonsSaga(): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  | UpsertCartAddonsResponse
  | RemoveCartItemsResponse
  | ShoppingCart
  | string
  | string[]
  | AddressType
  | CartItems
  | GetAddOnsPayload
  | Routes
  | RouteStates
  | Set<string | undefined>
  | null
  | ShoppingCartAddonsInput['customerType']
  | ShoppingCartAddonsInput['country']
> {
  const cartId = (yield select(selectCartId)) as string;
  const availableAddons = (yield select(selectAvailableAddons)) as GetAddOnsPayload;
  const serviceAddress = (yield select(selectServiceAddressValues)) as AddressType;
  const cartItems = (yield select(selectCartItems)) as CartItems;
  const productCandidateId = cartItems.plan?.productCandidateId;
  const addonIdToUpsert = (yield select(selectAddonsToUpsert)) as string | null;

  const customerType = (yield select(selectCustomerType)) as ShoppingCartAddonsInput['customerType'];
  const country = (yield select(selectCountry)) as ShoppingCartAddonsInput['country'];

  // For grouped addons, PAL requires an explicit DELETE before adding a different addon from the
  // same group. Detect if the cart already has a grouped addon that is being swapped out.
  const addonToBeRemoved = yield select(selectAddonToBeRemoved);
  const groupAddonIds = (yield select(selectGroupedAddonIds)) as Set<string | undefined>;
  // Only grouped addon upserts should trigger a pre-delete (swap).
  // Standalone addons can be stacked.
  const isGroupedAddonUpsert = addonIdToUpsert != null && groupAddonIds.has(addonIdToUpsert);

  try {
    if (!addonIdToUpsert) {
      yield put(addonActions.setLoadingAddonId(null));
      return;
    }

    if (addonToBeRemoved && isGroupedAddonUpsert) {
      yield call((removeRequest) => removeCartItemsAsync(removeRequest), {
        input: {
          cartId,
          productCandidateId: addonToBeRemoved,
          customerType,
          country,
        },
      });
    }

    if (!productCandidateId) {
      throw new Error('Missing plan product candidate id before upserting cart addons');
    }

    const cartInput: ShoppingCartAddonsInput = {
      cartId,
      addonProductId: addonIdToUpsert,
      postalCode: serviceAddress.postalCode,
      productCandidateId,
      customerType,
      country,
    };

    let cart: ShoppingCart | undefined;
    if (USE_NEW_API) {
      const restCart = (yield call(
        (requestPayload: ShoppingCartAddonsInput) => upsertCartAddonsRest(requestPayload),
        cartInput,
      )) as ShoppingCart;
      cart = restCart;
    } else {
      const { data } = (yield call((upsertRequest) => upsertCartAddonsAsync(upsertRequest), {
        input: cartInput,
      })) as UpsertCartAddonsResponse;
      cart = data?.upsertCartAddons ?? undefined;
    }

    if (cart) {
      yield call(generateCart, cart);

      const cartProducts = [cart.cartItems?.plan, ...(cart.cartItems?.addons ?? [])];

      yield put(consentActions.setCartProducts({ cartProducts }));

      if (availableAddons.addOnGroup) {
        // We only want to store the productCandidateId of the Grouped Addon we've just added to the cart
        const matchedGroupedAddon = cart.cartItems?.addons?.find(
          (addon) => addon?.productTypeId != null && groupAddonIds.has(addon.productTypeId),
        );

        if (matchedGroupedAddon) {
          yield put(cartActions.setAddonToBeRemoved(matchedGroupedAddon.productCandidateId ?? ''));
        }
      }
      const addon =
        availableAddons?.addOns?.find((availableAddon) => availableAddon?.id === addonIdToUpsert) ??
        availableAddons?.addOnGroup?.addOns?.find((groupAddon) => groupAddon?.id === addonIdToUpsert);

      adobeTargetDataLayer.push({
        event: DataLayerEvents.AA_EVENT,
        eventData: {
          event: DataLayerEventType.ADDON_SELECTED,
          addons: {
            id: addonIdToUpsert,
            selected: true,
            ...(addon?.name ? { selectedAddOn: addon.name } : {}),
          },
        },
      });

      // Clear the list of addons selected from the UI, so that when the customer tries to upsert again, we only send the newly selected ids
      yield put(addonActions.clearAddonIdToUpsert());
    }
    yield put(addonActions.setLoadingAddonId(null));

  } catch {
    yield call(generateError, [new GraphQLError('Failed to upsert cart addons')], 'upsertAddonsSaga');
    if (addonIdToUpsert) {
      yield put(addonActions.toggleAddon({ id: addonIdToUpsert, selected: false }));
    }
    yield put(addonActions.clearAddonIdToUpsert());
    yield put(addonActions.setLoadingAddonId(null));
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

/**
 *  Remove Cart Item request/response handler
 */
export function* removeCartItemsSaga(
  action: PayloadAction<CartProduct>,
): Generator<SelectEffect | PutEffect | CallEffect, void, string & RemoveCartItemsResponse & CartItems> {
  const showProductDisclosure = yield select(selectFeatureFlags);
  const customerType = yield select(selectCustomerType);
  const country = yield select(selectCountry);
  const addonToBeRemoved = yield select(selectAddonToBeRemoved);

  if (action.payload.productCandidateId) {
    yield put(addonActions.setLoadingAddonId(action.payload.productTypeId ?? null));
    yield put(consentActions.setEicMessagesLoading(true));
    const cartId: string = yield select(selectCartId);
    try {
      const { data }: RemoveCartItemsResponse = yield call((removeRequest) => removeCartItemsAsync(removeRequest), {
        input: { cartId, productCandidateId: action.payload.productCandidateId, customerType, country },
      });

      if (data?.removeCartItems) {
        const cartItems = (yield select(selectCartItems)) as CartItems;
        data.removeCartItems.cartItems.plan!.contractTerms = cartItems.plan?.contractTerms;
        data.removeCartItems.cartItems.plan!.contractTermsDisclosure = cartItems.plan?.contractTermsDisclosure;
        data.removeCartItems.cartItems.plan!.leaseFeePrice = cartItems.plan?.leaseFeePrice;
        data.removeCartItems.cartItems.plan!.leaseFeeShortName = cartItems.plan?.leaseFeeShortName;
        if (addonToBeRemoved && addonToBeRemoved === action.payload.productCandidateId) {
          //If we previously added a Grouped Addon to the cart and have just removed it, make sure to remove it from the client state too
          yield put(cartActions.setAddonToBeRemoved(''));
        }

        yield call(generateCart, data.removeCartItems);
        yield put(addonActions.toggleAddon({ id: action.payload.productTypeId ?? '', selected: false }));

        const cartProducts = [data.removeCartItems.cartItems?.plan, ...(data.removeCartItems.cartItems?.addons ?? [])];

        yield put(consentActions.setCartProducts({ cartProducts }));

        adobeTargetDataLayer.push({
          event: DataLayerEvents.AA_EVENT,
          eventData: {
            event: DataLayerEventType.ADDON_SELECTED,
            addons: { id: action.payload.productTypeId ?? '', selected: false, selectedAddOn: action.payload.name ?? '' },
          },
        });

        if (showProductDisclosure) {
          yield put(consentActions.getEicMessages());
        }
      }
      yield put(addonActions.setLoadingAddonId(null));
    } catch {
      yield put(appActions.setErrorRetryAction(action));
      yield put(appActions.setModalVisible(Modals.ERROR));
      yield call(generateError, [new GraphQLError('Failed to remove cart item')], 'removeCartItemsSaga');
      yield put(addonActions.setLoadingAddonId(null));
    }
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export function* cartSaga(): Generator<ForkEffect, void, void> {
  yield takeLatest(
    cartActions.upsertAddons.type,
    benchmark<
      void,
      SelectEffect | CallEffect | PutEffect,
      void,
      | UpsertCartAddonsResponse
      | RemoveCartItemsResponse
      | ShoppingCart
      | string
      | string[]
      | AddressType
      | CartItems
      | GetAddOnsPayload
      | Routes
      | RouteStates
      | Set<string | undefined>
      | ShoppingCartAddonsInput['customerType']
      | ShoppingCartAddonsInput['country']
    >(upsertAddonsSaga),
  );
  yield takeLatest(
    cartActions.removeCartItems.type,
    benchmark<CartProduct, SelectEffect | PutEffect | CallEffect, void, string & RemoveCartItemsResponse>(
      removeCartItemsSaga,
    ),
  );
}
